import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Plus, Edit2, Trash2, Eye, Loader2, BookUser } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';

import Pagination from '../../components/ui/pagination';

const DriverList = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [drivers, setDrivers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuthHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json'
  }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (vendorFilter !== 'all') params.append('vendor_id', vendorFilter);
      if (search) params.append('search', search);
      params.append('skip', String((page - 1) * pageSize));
      params.append('limit', String(pageSize));
      const [dRes, vRes] = await Promise.all([
        fetch(`${backendUrl}/api/drivers?${params}`, { headers: getAuthHeaders() }),
        fetch(`${backendUrl}/api/vendors?limit=500`, { headers: getAuthHeaders() }),
      ]);
      if (dRes.ok) { const d = await dRes.json(); setDrivers(d.items || []); setTotal(d.total || 0); }
      if (vRes.ok) { const v = await vRes.json(); setVendors(v.items || []); }
    } catch { toast({ title: "Error", description: "Failed to load data", variant: "destructive" }); }
    finally { setLoading(false); }
  }, [backendUrl, getAuthHeaders, vendorFilter, search, page, pageSize, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [vendorFilter]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); fetchData(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await fetch(`${backendUrl}/api/drivers/${deleteTarget.id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (!res.ok) throw new Error();
      toast({ title: "Deleted", description: "Driver removed" });
      fetchData();
    } catch { toast({ title: "Error", description: "Failed to delete", variant: "destructive" }); }
    finally { setDeleteTarget(null); }
  };

  const isExpired = (d) => d && new Date(d) < new Date();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-text-dark font-manrope">Drivers</h1>
          <p className="text-text-secondary font-ibm mt-1">Vendor-wise driver management</p>
        </div>
        <Button data-testid="add-driver-btn" onClick={() => navigate('/fleet/drivers/add')}
          className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg">
          <Plus className="w-5 h-5 mr-2" />Add Driver
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-primary-blue text-white rounded-xl shadow-lg p-6">
        <div className="flex flex-wrap items-center gap-4">
          <h3 className="text-xl font-semibold font-manrope">All Drivers ({total})</h3>
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
            <Input data-testid="driver-search-input" placeholder="Name, Mobile, DL No..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 font-ibm" />
          </div>
          <Select value={vendorFilter} onValueChange={setVendorFilter}>
            <SelectTrigger data-testid="driver-vendor-filter" className="w-44 bg-white/10 border-white/20 text-white font-ibm">
              <SelectValue placeholder="All Vendors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Vendors</SelectItem>
              {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.business_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-accent-blue" />
          </div>
        ) : drivers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <p className="text-text-secondary font-ibm">No drivers found</p>
            <Button onClick={() => navigate('/fleet/drivers/add')} className="bg-accent-blue text-white font-ibm">
              <Plus className="w-4 h-4 mr-2" />Add First Driver
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="drivers-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['#','Vendor','Driver Name','Mobile 1','DL No.','DL Expiry','Joining Date','Status','Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {drivers.map((d, idx) => (
                  <tr key={d.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{idx + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium text-text-dark font-ibm whitespace-nowrap">{d.vendor_name || '—'}</td>
                    <td className="px-4 py-3 text-sm font-medium text-text-dark font-ibm">{d.name}</td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{d.mobile_1}</td>
                    <td className="px-4 py-3 text-sm font-mono text-text-dark">{d.dl_no || '—'}</td>
                    <td className="px-4 py-3">
                      {d.dl_expiry_date ? (
                        <span className={`text-sm font-ibm ${isExpired(d.dl_expiry_date) ? 'text-accent-red font-semibold' : 'text-text-secondary'}`}>
                          {d.dl_expiry_date}
                        </span>
                      ) : <span className="text-sm text-text-secondary">—</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-text-secondary font-ibm">{d.date_of_joining || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge className={`border-0 text-xs font-ibm ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {d.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button data-testid={`view-driver-${d.id}`} onClick={() => navigate(`/fleet/drivers/edit/${d.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg" title="View/Edit">
                          <Edit2 className="w-4 h-4 text-accent-blue" />
                        </button>
                        <button data-testid={`ledger-driver-${d.id}`} onClick={() => navigate(`/fleet/driver-ledger?driver_id=${d.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-purple-50 rounded-lg" title="View Ledger">
                          <BookUser className="w-4 h-4 text-purple-600" />
                        </button>
                        <button data-testid={`delete-driver-${d.id}`} onClick={() => setDeleteTarget(d)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4 text-accent-red" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && drivers.length > 0 && (
          <Pagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={v => { setPageSize(v); setPage(1); }} />
        )}
      </Card>

      <AlertDialog open={!!deleteTarget} onOpenChange={v => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Driver</AlertDialogTitle>
            <AlertDialogDescription>Delete driver "{deleteTarget?.name}"? This will also remove all KYC documents and bank details linked to this driver.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-accent-red hover:bg-accent-red/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DriverList;
